/**
 * MealPlanPreferences — bottom-sheet drawer for tweaking the AI meal-plan
 * inputs (restrictions, dislikes, cuisines, cooking-time). Portaled to
 * document.body so nested transformed ancestors can't clip it (learned this
 * the hard way with our other modals).
 *
 * Two modes:
 *   `mode='settings'` — Save changes to profile (persist).
 *   `mode='generate'` — Also fire the AI plan generation with these settings.
 */
import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { X, Loader2, Sparkles } from "lucide-react";

const RESTRICTIONS = [
  ["vegetarian", "Vegetarian"],
  ["vegan", "Vegan"],
  ["pescatarian", "Pescatarian"],
  ["gluten_free", "Gluten-free"],
  ["dairy_free", "Dairy-free"],
  ["nut_free", "Nut-free"],
  ["halal", "Halal"],
  ["kosher", "Kosher"],
];

const CUISINES = [
  ["mediterranean", "Mediterranean"],
  ["asian", "Asian"],
  ["mexican", "Mexican"],
  ["indian", "Indian"],
  ["american", "American"],
  ["middle_eastern", "Middle Eastern"],
  ["italian", "Italian"],
  ["anything", "Anything"],
];

const COOKING_TIME = [
  ["quick", "Quick (< 20 min)"],
  ["normal", "Normal (~ 30 min)"],
  ["loves_cooking", "I love cooking (45+ min)"],
];

// Repetition modes — 'same_daily' collapses the shopping list dramatically,
// which is the point: batch-cooking + minimum grocery spend.
const REPETITION = [
  ["variety", "Variety", "7 distinct dinners, reused ingredients"],
  ["batch_3days", "Batch every 3 days", "Two cook sessions per week"],
  ["same_daily", "Same meals daily", "One cook, cheapest groceries"],
];

const toggle = (arr, value) =>
  arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];

const Chip = ({ active, onClick, label, testid }) => (
  <button
    type="button"
    onClick={onClick}
    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
      active
        ? "bg-[#6B9BFF] border-[#6B9BFF] text-black font-bold"
        : "border-white/20 text-white/70 hover:border-white/40 hover:text-white"
    }`}
    data-testid={testid}
  >
    {label}
  </button>
);

export default function MealPlanPreferences({
  open,
  initial,
  onClose,
  onSave,
  onGenerate,
  busy,
  elapsedSec = 0,
}) {
  const [prefs, setPrefs] = useState(initial || {
    restrictions: [], dislikes: "", cuisines: [], cooking_time: ["normal"], repetition_mode: "variety",
  });

  // Reseed when the drawer reopens with fresh initial values.
  useEffect(() => {
    if (open && initial) {
      // Backfill repetition_mode for older persisted prefs that predate this field.
      setPrefs({ repetition_mode: "variety", ...initial });
    }
  }, [open, initial]);

  if (!open) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-end sm:items-center justify-center"
      onClick={onClose}
      data-testid="meal-prefs-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-xl bg-[#121212] border border-white/10 sm:rounded-2xl p-6 max-h-[92vh] overflow-y-auto"
        data-testid="meal-prefs-drawer"
      >
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="label-eyebrow text-[#6B9BFF] mb-1">Meal Plan Preferences</div>
            <h3 className="font-display text-2xl leading-none">Tell us how you eat</h3>
          </div>
          <button
            onClick={onClose}
            className="text-white/40 hover:text-white"
            data-testid="meal-prefs-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Restrictions */}
        <div className="mb-5">
          <div className="label-eyebrow text-white/50 text-[10px] mb-2">Dietary restrictions</div>
          <div className="flex flex-wrap gap-2">
            {RESTRICTIONS.map(([val, label]) => (
              <Chip
                key={val}
                label={label}
                active={prefs.restrictions.includes(val)}
                onClick={() => setPrefs((p) => ({ ...p, restrictions: toggle(p.restrictions, val) }))}
                testid={`meal-prefs-restriction-${val}`}
              />
            ))}
          </div>
        </div>

        {/* Free-text dislikes */}
        <div className="mb-5">
          <div className="label-eyebrow text-white/50 text-[10px] mb-2">Allergies / dislikes</div>
          <input
            type="text"
            value={prefs.dislikes}
            onChange={(e) => setPrefs((p) => ({ ...p, dislikes: e.target.value }))}
            placeholder="e.g. no mushrooms, no seafood, no cilantro"
            className="w-full bg-white/5 border border-white/10 focus:border-[#6B9BFF] outline-none rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30"
            data-testid="meal-prefs-dislikes"
          />
        </div>

        {/* Cuisines */}
        <div className="mb-5">
          <div className="label-eyebrow text-white/50 text-[10px] mb-2">Cuisine preferences</div>
          <div className="flex flex-wrap gap-2">
            {CUISINES.map(([val, label]) => (
              <Chip
                key={val}
                label={label}
                active={prefs.cuisines.includes(val)}
                onClick={() => setPrefs((p) => ({ ...p, cuisines: toggle(p.cuisines, val) }))}
                testid={`meal-prefs-cuisine-${val}`}
              />
            ))}
          </div>
        </div>

        {/* Cooking time */}
        <div className="mb-6">
          <div className="label-eyebrow text-white/50 text-[10px] mb-2">Cooking time budget</div>
          <div className="flex flex-wrap gap-2">
            {COOKING_TIME.map(([val, label]) => (
              <Chip
                key={val}
                label={label}
                active={prefs.cooking_time.includes(val)}
                onClick={() => setPrefs((p) => ({ ...p, cooking_time: toggle(p.cooking_time, val) }))}
                testid={`meal-prefs-time-${val}`}
              />
            ))}
          </div>
        </div>

        {/* Repetition mode — grocery-list optimisation. Single-select. */}
        <div className="mb-6">
          <div className="label-eyebrow text-white/50 text-[10px] mb-2">Meal variety</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {REPETITION.map(([val, label, sub]) => (
              <button
                key={val}
                type="button"
                onClick={() => setPrefs((p) => ({ ...p, repetition_mode: val }))}
                className={`text-left p-3 border transition-colors ${
                  prefs.repetition_mode === val
                    ? "bg-[#6B9BFF]/10 border-[#6B9BFF]"
                    : "border-white/15 hover:border-white/30"
                }`}
                data-testid={`meal-prefs-repetition-${val}`}
              >
                <div className={`text-xs font-bold mb-0.5 ${prefs.repetition_mode === val ? "text-[#6B9BFF]" : "text-white"}`}>
                  {label}
                </div>
                <div className="text-[10px] text-white/50 leading-snug">{sub}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            onClick={() => onSave(prefs)}
            disabled={busy}
            className="flex-1 bg-white/5 border border-white/20 hover:border-white/40 text-white font-bold uppercase tracking-widest text-xs px-5 py-3 disabled:opacity-50"
            data-testid="meal-prefs-save"
          >
            Save preferences
          </button>
          <button
            onClick={() => onGenerate(prefs)}
            disabled={busy}
            className="flex-1 bg-[#6B9BFF] hover:bg-white text-black font-black uppercase tracking-widest text-xs px-5 py-3 inline-flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="meal-prefs-generate"
          >
            {busy ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Generating… {elapsedSec ? `${elapsedSec}s` : ""}</>
            ) : (
              <><Sparkles className="w-4 h-4" /> Generate plan</>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
